import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import { GetCompanyRes } from "gen/api/company/service_pb"
import {
  GetOrderRes,
  GetOrderRes_AdditionalFeeList,
  ListOrderNotificationsForOrderRes_Product,
  ListOrderNotificationsForOrderRes_ShipmentNotification,
} from "gen/api/order/service_pb"
import { Product as CatalogProduct } from "gen/catalog/models_pb"
import {
  AdditionalFees_Fee as AdditionalFee,
  Location,
} from "gen/company/models_pb"
import { Timestamp } from "gen/google/protobuf/timestamp_pb"
import { LedgerEntry_UomSnapshot } from "gen/ledger/models_pb"
import { Tier } from "gen/price/models_pb"
import {
  Product as PBProduct,
  Product_Cost as PBProductCost,
  Inventories,
  Product,
} from "gen/product/models_pb"
import { Address } from "gen/proto/address/models_pb"
import { IntegrationVendor } from "gen/proto/integration_vendor/models_pb"
import {
  FractionalMoney as PBFractionalMoney,
  Money as PBMoney,
} from "gen/proto/money/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import {
  Order_Product as OrderProduct,
  Order_Status as OrderStatus,
  Order_Method,
} from "gen/txn/models_pb"
import { OrderReceiveSettings, Vendor } from "gen/vendors/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { FractionalMoney } from "shared-ts/classes/money"
import { UUID } from "shared-ts/classes/uuid"
import {
  convertUoMQuantityToQuantity,
  getConversionFactorForUom,
  safeConvertQuantityToUoMQuantity,
} from "shared-ts/libs/product/unitsOfMeasure.helpers"

import { AppState } from "app/store"
import { stringify } from "lib/stringify"

import OrderForm from "./types/OrderForm"
import { fixTxnProductsListOrder } from "../transactions/helpers"
import { mergeShipmentNotificationProducts } from "./helpers/shipmentPriceChanges"
import {
  applyOrderUomSelection,
  getOrderDefaultUomId,
  getOrderUomInfo,
  isValidUomSnapshot,
} from "./helpers/uom"
import { updateOrderFormWithProductData } from "../transactions/helpers/helpers"
import { TransactionProduct } from "../transactions/transactionProduct"

export default interface OrderFormState extends OrderForm {
  initialState?: OrderForm
  isEdited?: boolean
  isPreloadingProducts?: boolean
  companyData?: GetCompanyRes
  initialProductIndexById?: Record<string, number>
  dirtyProductIds?: Record<string, true>
  dirtyOrderProductIds?: Record<string, true>
  // A fresh object is assigned per submit, so effect deps re-fire on
  // reference identity even when the same line is modified twice.
  lastModifiedOrderProduct?: { id: PBUUID }
}

export interface EditProductCostAndPricePayload {
  productId: PBUUID
  tiers: Tier[]
  isMarginPriceRoundedUp: boolean
  defaultPrice: PBMoney
  defaultCostV2: PBFractionalMoney
  pricingTiersDefaultIdString: string
  costs: PBProductCost[]
}

const initialState: OrderFormState = {
  addProductQuantity: Decimal.fromBigSource(1),
}

function flattenAdditionalFeesByProductId(
  src: { [id: string]: GetOrderRes_AdditionalFeeList } | undefined
): { [id: string]: AdditionalFee[] } {
  const out: { [id: string]: AdditionalFee[] } = {}
  for (const [id, list] of Object.entries(src ?? {})) {
    out[id] = list?.fees ?? []
  }
  return out
}

// A draft has not been placed with the vendor: no persisted order yet, or one
// still in CREATED. Drafts edit quantityOrderedV2; placed orders edit
// quantityStagedReceived.
export function isDraftOrderStatus(status: OrderStatus | undefined): boolean {
  return (
    status === undefined ||
    status === OrderStatus.UNDEFINED ||
    status === OrderStatus.CREATED
  )
}

// Defaults the method from the shipment's vendor while the order is still new
// or draft. On a placed order the method records how the PO was actually
// sent, so it must not change — upserting later (e.g. receiving) would
// persist the changed value.
function methodForShipmentVendor(
  method: Order_Method | undefined,
  vendor: Vendor,
  orderStatus: OrderStatus | undefined
): Order_Method | undefined {
  if (!isDraftOrderStatus(orderStatus)) {
    return method
  }
  if (vendor.integrationVendor !== IntegrationVendor.UNSPECIFIED) {
    return Order_Method.EDI
  }
  if (method === undefined || method === Order_Method.NONE) {
    return vendor.ordersEmail ? Order_Method.EMAIL : Order_Method.NONE
  }
  return method
}

// Staged receive edits are saved but aren't vendor-visible order changes,
// so order-level dirtiness compares lines without them.
function stripReceiveStaging(p: OrderProduct) {
  const { quantityStagedReceived: _quantityStagedReceived, ...rest } = p
  return rest
}

// Both are derived from initialState.products, so a keystroke re-serializes one
// line instead of the whole array: `initialProductIndexById` locates a loaded
// line by id, `dirtyProductIds` holds the lines that now differ from it. Left
// undefined until there is a loaded order to compare against.
function recomputeProductsEdited(state: OrderFormState) {
  const initialProducts = state.initialState?.products
  if (!initialProducts?.length) {
    state.initialProductIndexById = undefined
    state.dirtyProductIds = undefined
    state.dirtyOrderProductIds = undefined
    return
  }
  const indexById: Record<string, number> = {}
  const signatures = new Map<string, string>()
  const orderSignatures = new Map<string, string>()
  initialProducts.forEach((p, i) => {
    if (!p.id) return
    const idString = UUID.idString(p.id)
    indexById[idString] = i
    signatures.set(idString, stringify(p))
    orderSignatures.set(idString, stringify(stripReceiveStaging(p)))
  })
  const dirty: Record<string, true> = {}
  const orderDirty: Record<string, true> = {}
  for (const p of state.products ?? []) {
    if (!p.id) continue
    const idString = UUID.idString(p.id)
    if (stringify(p) !== signatures.get(idString)) {
      dirty[idString] = true
    }
    if (stringify(stripReceiveStaging(p)) !== orderSignatures.get(idString)) {
      orderDirty[idString] = true
    }
  }
  state.initialProductIndexById = indexById
  state.dirtyProductIds = dirty
  state.dirtyOrderProductIds = orderDirty
}

function ensureProductsEdited(state: OrderFormState) {
  if (!state.dirtyProductIds) {
    recomputeProductsEdited(state)
  }
}

function markProductEdited(
  state: OrderFormState,
  product: OrderProduct | undefined
) {
  if (!product?.id) return
  ensureProductsEdited(state)
  const dirty = state.dirtyProductIds
  if (!dirty) return
  const idString = UUID.idString(product.id)
  const initialIndex = state.initialProductIndexById?.[idString]
  const initial =
    initialIndex === undefined
      ? undefined
      : state.initialState?.products?.[initialIndex]
  if (initial && stringify(initial) === stringify(product)) {
    delete dirty[idString]
  } else {
    dirty[idString] = true
  }
  const orderDirty = state.dirtyOrderProductIds ?? {}
  if (
    initial &&
    stringify(stripReceiveStaging(initial)) ===
      stringify(stripReceiveStaging(product))
  ) {
    delete orderDirty[idString]
  } else {
    orderDirty[idString] = true
  }
  state.dirtyOrderProductIds = orderDirty
}

function areProductsEdited(state: OrderFormState): boolean {
  const initialLength = state.initialState?.products?.length ?? 0
  const currentLength = state.products?.length ?? 0
  if (currentLength !== initialLength) {
    return true
  }
  if (currentLength === 0) {
    return false
  }
  ensureProductsEdited(state)
  return Object.keys(state.dirtyProductIds ?? {}).length > 0
}

const checkOrderIsEdited = (state: OrderFormState) => {
  const initState = { ...state.initialState }
  const currState = { ...state, isEdited: undefined, initialState: undefined }
  if (!checkStateEquality(initState ?? {}, currState)) {
    return true
  }
  return areProductsEdited(state)
}

function updateOrderProductOnSubmit(args: {
  orderProduct: OrderProduct
  baseQuantityToAdd: Decimal
  status?: OrderStatus
  nextPosition: number
}): OrderProduct {
  const { orderProduct, baseQuantityToAdd, status, nextPosition } = args

  const updated: OrderProduct = { ...orderProduct, position: nextPosition }

  if (status === OrderStatus.ORDERED) {
    const currentStaged = orderProduct.quantityStagedReceived
      ? Decimal.fromPB(orderProduct.quantityStagedReceived)
      : Decimal.zero()
    updated.quantityStagedReceived = currentStaged.add(baseQuantityToAdd)
  } else {
    const currentOrdered = orderProduct.quantityOrderedV2
      ? Decimal.fromPB(orderProduct.quantityOrderedV2)
      : Decimal.zero()
    updated.quantityOrderedV2 = currentOrdered.add(baseQuantityToAdd)
  }

  return updated
}

// Compares the non-product fields. Product dirtiness is tracked separately and
// incrementally (see areProductsEdited / markProductEdited).
const checkStateEquality = (stateA: OrderFormState, stateB: OrderFormState) => {
  if (stateA.internalNotes !== stateB.internalNotes) {
    return false
  }

  if (stateA.orderNotes !== stateB.orderNotes) {
    return false
  }

  if (!UUID.eqFromPB(stateA.location?.id, stateB.location?.id)) {
    return false
  }

  if (
    !Timestamp.equals(
      stateA.requestedDeliveryDate,
      stateB.requestedDeliveryDate
    )
  ) {
    return false
  }

  if (!UUID.eqFromPB(stateA.vendor?.id, stateB.vendor?.id)) {
    return false
  }

  if (stateA.method !== stateB.method) {
    return false
  }

  if (stringify(stateA.searchProduct) !== stringify(stateB.searchProduct)) {
    return false
  }

  if (stringify(stateA.dropshipAddress) !== stringify(stateB.dropshipAddress)) {
    return false
  }

  if ((stateA.promoCode ?? "") !== (stateB.promoCode ?? "")) {
    return false
  }
  if (!Timestamp.equals(stateA.invoiceDate, stateB.invoiceDate)) {
    return false
  }
  if (!Timestamp.equals(stateA.dueDate, stateB.dueDate)) {
    return false
  }
  if ((stateA.invoiceNumber ?? "") !== (stateB.invoiceNumber ?? "")) {
    return false
  }
  if ((stateA.referenceNumber ?? "") !== (stateB.referenceNumber ?? "")) {
    return false
  }

  return true
}

export const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    setOrderInitialState: (
      state,
      action: PayloadAction<{
        orderData: GetOrderRes
        companyData: GetCompanyRes
      }>
    ) => {
      const { orderData, companyData } = action.payload
      const initialProducts = action.payload.orderData?.order?.products
        ? orderData?.order?.products.map((prod: OrderProduct) => {
            if (orderData?.order?.status !== OrderStatus.ORDERED) {
              return applyOrderUomSelection({ orderProduct: prod })
            }
            if (prod.quantityStagedReceived) {
              return applyOrderUomSelection({ orderProduct: prod })
            }

            if (companyData.company?.shouldSetOrderedProductQuantityToZero) {
              return applyOrderUomSelection({
                orderProduct: {
                  ...prod,
                  quantityStagedReceived: Decimal.zero(),
                },
              })
            }

            const ordered = prod.quantityOrderedV2
              ? Decimal.fromPB(prod.quantityOrderedV2)
              : Decimal.zero()
            const received = prod.quantityReceivedV2
              ? Decimal.fromPB(prod.quantityReceivedV2)
              : Decimal.zero()
            const outstanding = ordered.sub(received)

            return applyOrderUomSelection({
              orderProduct: {
                ...prod,
                quantityStagedReceived: ordered.lt(Decimal.zero())
                  ? outstanding
                  : Decimal.max(outstanding, Decimal.zero()),
              },
            })
          })
        : undefined

      const tierPricesById: Record<string, Tier[]> = {}
      Object.keys(orderData?.tierPricesByProductId ?? {}).forEach(
        (id: string) => {
          tierPricesById[id] =
            orderData?.tierPricesByProductId?.[id]?.tierPrices ?? []
        }
      )

      state.initialState = {
        internalNotes: orderData?.order?.internalNotes,
        orderNotes: orderData?.order?.orderNotes,
        location: orderData?.location,
        products: initialProducts,
        requestedDeliveryDate: orderData?.order?.requestedDeliveryAt,
        vendor: orderData?.vendor,
        method: orderData?.order?.method,

        productsById: orderData?.productsById,
        catalogProductsById: orderData?.catalogProductsById,
        inventoriesByProductId: orderData?.inventoriesByProductId,
        additionalFeesByProductId: flattenAdditionalFeesByProductId(
          orderData?.additionalFeesByProductId
        ),
        tierPricesByProductId: tierPricesById,
        dropshipAddress: orderData?.order?.dropshipAddress,
        dropshipRecipient: orderData?.order?.dropshipRecipient,
        promoCode: orderData?.order?.promoCode,
        invoiceDate: orderData?.order?.invoiceDetails?.invoiceDate,
        dueDate: orderData?.order?.invoiceDetails?.dueDate,
        invoiceNumber: orderData?.order?.invoiceDetails?.invoiceNumber,
        referenceNumber: orderData?.order?.invoiceDetails?.referenceNumber,
      }
      state.isEdited = false
      state.internalNotes = orderData?.order?.internalNotes
      state.orderNotes = orderData?.order?.orderNotes
      state.location = orderData?.location
      state.products = initialProducts
      // Otherwise the previous order's dirty lines leak into this one.
      state.dirtyProductIds = undefined
      state.dirtyOrderProductIds = undefined
      // Otherwise reopening the order replays the last scan's scroll/flash.
      state.lastModifiedOrderProduct = undefined
      state.requestedDeliveryDate = orderData?.order?.requestedDeliveryAt
      state.vendor = orderData?.vendor
      state.method = orderData?.order?.method

      state.productsById = orderData?.productsById
      state.catalogProductsById = orderData?.catalogProductsById
      state.inventoriesByProductId = orderData?.inventoriesByProductId
      state.additionalFeesByProductId = flattenAdditionalFeesByProductId(
        orderData?.additionalFeesByProductId
      )
      state.tierPricesByProductId = tierPricesById
      state.imageUrlsByProductId = orderData?.imageUrlsByProductId
      state.addProductQuantity = Decimal.fromBigSource(1)
      state.dropshipAddress = orderData?.order?.dropshipAddress
      state.dropshipRecipient = orderData?.order?.dropshipRecipient
      state.promoCode = orderData?.order?.promoCode
      state.shipmentNotification = undefined
      state.orderReceiveSettingsOverride = undefined
      state.invoiceDate = orderData?.order?.invoiceDetails?.invoiceDate
      state.dueDate = orderData?.order?.invoiceDetails?.dueDate
      state.invoiceNumber = orderData?.order?.invoiceDetails?.invoiceNumber
      state.referenceNumber = orderData?.order?.invoiceDetails?.referenceNumber
      return state
    },
    setOrderInternalNotes: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.internalNotes = action.payload
      state.isEdited = checkOrderIsEdited(state)
    },
    setOrderNotes: (state, action: PayloadAction<string | undefined>) => {
      state.orderNotes = action.payload
      state.isEdited = checkOrderIsEdited(state)
    },
    setOrderIsEdited: (state, action: PayloadAction<boolean>) => {
      state.isEdited = action.payload
    },
    setOrderLocation: (state, action: PayloadAction<Location | undefined>) => {
      state.location = action.payload
      delete state.dropshipAddress
      delete state.dropshipRecipient
      state.isEdited = checkOrderIsEdited(state)
      return state
    },
    setOrderProducts: (
      state,
      action: PayloadAction<OrderProduct[] | undefined>
    ) => {
      state.products = action.payload
      recomputeProductsEdited(state)
      state.isEdited = checkOrderIsEdited(state)
      return state
    },
    setProductsById: (
      state,
      action: PayloadAction<{ [id: string]: Product }>
    ) => {
      state.productsById = action.payload
      return state
    },
    setInventoriesByProductId: (
      state,
      action: PayloadAction<{ [id: string]: Inventories }>
    ) => {
      state.inventoriesByProductId = action.payload
      return state
    },
    setCatalogProductsById: (
      state,
      action: PayloadAction<{ [id: string]: CatalogProduct }>
    ) => {
      state.catalogProductsById = action.payload
      return state
    },
    setAdditionalFeesByProductId: (
      state,
      action: PayloadAction<{ [id: string]: AdditionalFee[] }>
    ) => {
      state.additionalFeesByProductId = action.payload
      return state
    },
    setTierPricesByProductId: (
      state,
      action: PayloadAction<{ [id: string]: Tier[] }>
    ) => {
      state.tierPricesByProductId = action.payload
      return state
    },
    setOrderRequestedDeliveryDate: (
      state,
      action: PayloadAction<Timestamp | undefined>
    ) => {
      state.requestedDeliveryDate = action.payload
      state.isEdited = checkOrderIsEdited(state)
      return state
    },
    setOrderVendor: (state, action: PayloadAction<Vendor | undefined>) => {
      state.vendor = action.payload

      if (state.method === undefined || state.method === Order_Method.NONE) {
        if (
          action.payload?.integrationVendor !== undefined &&
          action.payload?.integrationVendor !== IntegrationVendor.UNSPECIFIED
        ) {
          state.method = Order_Method.EDI
        } else if (action.payload?.ordersEmail) {
          state.method = Order_Method.EMAIL
        } else {
          state.method = Order_Method.NONE
        }
      }

      if (state.method === Order_Method.EMAIL && !action.payload?.ordersEmail) {
        if (
          action.payload?.integrationVendor !== undefined &&
          action.payload?.integrationVendor !== IntegrationVendor.UNSPECIFIED
        ) {
          state.method = Order_Method.EDI
        } else {
          state.method = Order_Method.NONE
        }
      }

      if (
        state.method === Order_Method.EDI &&
        !action.payload?.integrationVendor
      ) {
        if (action.payload?.ordersEmail) {
          state.method = Order_Method.EMAIL
        } else {
          state.method = Order_Method.NONE
        }
      }

      state.isEdited = checkOrderIsEdited(state)
      return state
    },
    setOrderMethod: (
      state,
      action: PayloadAction<Order_Method | undefined>
    ) => {
      state.method = action.payload
      state.isEdited = checkOrderIsEdited(state)
      return state
    },
    resetOrder: (
      state,
      action: PayloadAction<{ location?: Location; forceReset?: boolean }>
    ) => {
      const { location, forceReset = false } = action.payload

      // Clear products unless we're preserving preloaded products (and not forcing reset)
      if (!state.isPreloadingProducts || forceReset) {
        state.productsById = {}
        state.catalogProductsById = {}
        state.tierPricesByProductId = {}
        state.additionalFeesByProductId = {}
        state.inventoriesByProductId = {}
        state.products = []
        state.isEdited = false
      } else {
        state.isEdited = true
      }
      state.location = location
      state.orderNotes = ""
      state.internalNotes = ""
      state.vendor = null
      state.requestedDeliveryDate = undefined
      state.searchProduct = undefined
      state.addProductQuantity = Decimal.fromBigSource(1)
      state.isPreloadingProducts = false
      state.dropshipAddress = undefined
      state.dropshipRecipient = ""
      state.promoCode = undefined
      state.shipmentNotification = undefined
      state.lastModifiedOrderProduct = undefined
      state.orderReceiveSettingsOverride = undefined
    },
    searchProductSelected: (
      state,
      action: PayloadAction<{
        result: TransactionProduct
        status?: OrderStatus
        // Only the hardware-scanner path auto-submits. Search-combobox and
        // AddProductModal selections also produce isScan results (they share
        // the scan RPC) but must stay select-only.
        isPhysicalScan?: boolean
      }>
    ) => {
      selectOrderProduct(state, action.payload.result)

      const result = action.payload.result
      if (
        action.payload.isPhysicalScan &&
        !result.product?.isSpecialOrderableV2 &&
        !result.product?.isNonStock
      ) {
        ordersSlice.caseReducers.searchProductSubmitted(state, {
          type: "searchProductSubmitted",
          payload: {
            status: action.payload.status,
            quantity: result.quantity.gt(Decimal.zero())
              ? result.quantity
              : Decimal.fromBigSource(1),
            uomId: result.uomId,
            uomSnapshot: result.uomSnapshot,
          },
        })
      }
    },
    searchProductSubmitted: (
      state,
      action: PayloadAction<{
        status?: OrderStatus
        quantity?: Decimal
        uomId?: PBUUID
        uomSnapshot?: LedgerEntry_UomSnapshot
      }>
    ) => {
      submitOrderProduct(
        state,
        action.payload.status,
        action.payload.quantity,
        action.payload.uomId,
        action.payload.uomSnapshot
      )
    },
    removeOrderProduct: (state, action: PayloadAction<OrderProduct>) => {
      const existingProductIndex = state.products?.findIndex((product) =>
        UUID.eqFromPB(product.id!, action.payload.id)
      )

      if (
        typeof existingProductIndex === "number" &&
        existingProductIndex >= 0
      ) {
        state.products?.splice(existingProductIndex, 1)
      }

      if (state.products) {
        state.products = fixTxnProductsListOrder(
          state.products
        ) as OrderProduct[]
      }

      let shouldKeep = false
      for (const product of state.products ?? []) {
        if (UUID.eqFromPB(product.productId, action.payload.productId)) {
          shouldKeep = true
        }
      }

      if (!shouldKeep && action.payload.productId) {
        const productID = UUID.fromPB(action.payload.productId).toString()
        if (state.productsById?.[productID]) {
          delete state.productsById[productID]
        }

        if (state.catalogProductsById?.[productID]) {
          delete state.catalogProductsById[productID]
        }

        if (state.inventoriesByProductId?.[productID]) {
          delete state.inventoriesByProductId[productID]
        }

        if (state.additionalFeesByProductId?.[productID]) {
          delete state.additionalFeesByProductId[productID]
        }

        if (state.tierPricesByProductId?.[productID]) {
          delete state.tierPricesByProductId[productID]
        }
      }

      recomputeProductsEdited(state)
      state.isEdited = checkOrderIsEdited(state)
    },
    setOrderProductQuantity: (state, action: PayloadAction<OrderProduct>) => {
      if (!action?.payload?.id) {
        return
      }

      if (!state.products) {
        state.products = []
      }

      const existingProductIndex = state.products?.findIndex((product) =>
        UUID.fromPB(product.id!).eq(UUID.fromPB(action.payload.id!))
      )

      if (
        typeof existingProductIndex === "number" &&
        existingProductIndex >= 0
      ) {
        state.products[existingProductIndex] = {
          ...state.products[existingProductIndex],
          quantityOrderedV2: action.payload.quantityOrderedV2,
          quantityStagedReceived: action.payload.quantityStagedReceived,
        }
        state.products[existingProductIndex] = applyOrderUomSelection({
          orderProduct: state.products[existingProductIndex],
        })
        markProductEdited(state, state.products[existingProductIndex])
      }
      state.isEdited = checkOrderIsEdited(state)
    },
    setOrderProductUom: (
      state,
      action: PayloadAction<{
        id: PBUUID
        uomId?: PBUUID
        uomSnapshot?: LedgerEntry_UomSnapshot
      }>
    ) => {
      if (!state.products) return
      const { id, uomId, uomSnapshot } = action.payload
      const idx = state.products.findIndex((p) => UUID.eqFromPB(p.id, id))
      if (idx < 0) return

      // Preserve the displayed ordered/staged quantities across UOM swaps
      const orderProduct = state.products[idx]
      const oldInfo = getOrderUomInfo({ orderProduct })
      const oldFactor = oldInfo.uomId
        ? oldInfo.conversionFactorToBase
        : undefined
      const displayedOrdered = safeConvertQuantityToUoMQuantity(
        Decimal.fromPB(orderProduct.quantityOrderedV2 ?? Decimal.zero()),
        oldFactor
      )
      const displayedStaged = safeConvertQuantityToUoMQuantity(
        Decimal.fromPB(orderProduct.quantityStagedReceived ?? Decimal.zero()),
        oldFactor
      )
      const newFactor =
        uomId && uomSnapshot?.conversionFactorToBase
          ? Decimal.fromPB(uomSnapshot.conversionFactorToBase)
          : Decimal.fromBigSource(1)

      state.products[idx] = applyOrderUomSelection({
        orderProduct: {
          ...orderProduct,
          uomId,
          uomSnapshot,
          quantityOrderedV2: displayedOrdered.mult(newFactor),
          quantityStagedReceived: displayedStaged.mult(newFactor),
        },
      })
      markProductEdited(state, state.products[idx])
      state.isEdited = checkOrderIsEdited(state)
    },
    setOrderProductUomForProductId: (
      state,
      action: PayloadAction<{
        productId: PBUUID
        uomId?: PBUUID
        uomSnapshot?: LedgerEntry_UomSnapshot
      }>
    ) => {
      if (!state.products) return
      const { productId, uomId, uomSnapshot } = action.payload
      const productIdString = UUID.idString(productId)
      for (let i = 0; i < state.products.length; i++) {
        const p = state.products[i]
        if (!p.productId) continue
        if (UUID.idString(p.productId) !== productIdString) continue
        if (p.uomId) continue
        state.products[i] = applyOrderUomSelection({
          orderProduct: { ...p, uomId, uomSnapshot },
        })
      }
      recomputeProductsEdited(state)
      state.isEdited = checkOrderIsEdited(state)
    },
    setOrderProductNotes: (
      state,
      action: PayloadAction<{ id: PBUUID; notes: string }>
    ) => {
      if (!state.products) {
        state.products = []
      }

      const existingProductIndex = state.products?.findIndex((product) =>
        UUID.fromPB(product.id!).eq(UUID.fromPB(action.payload.id!))
      )

      if (existingProductIndex >= 0) {
        state.products[existingProductIndex] = {
          ...state.products[existingProductIndex],
          notes: action.payload.notes,
        }
        markProductEdited(state, state.products[existingProductIndex])
      }
      state.isEdited = checkOrderIsEdited(state)
    },
    setProductCostAndPrice: (
      state,
      action: PayloadAction<EditProductCostAndPricePayload>
    ) => {
      if (!action.payload.productId || !state.productsById) {
        return
      }

      const existingProduct: PBProduct =
        state.productsById[UUID.fromPB(action.payload.productId).toString()]

      existingProduct.costs = action.payload.costs

      existingProduct.costs.filter(
        (cost: PBProductCost) => cost.isDefault
      )[0].costV2 = action.payload.defaultCostV2

      state.productsById = {
        ...state.productsById,
        [UUID.fromPB(action.payload.productId).toString()]: existingProduct,
      }

      state.tierPricesByProductId = {
        ...state.tierPricesByProductId,
        [UUID.fromPB(action.payload.productId).toString()]:
          action.payload.tiers,
      }
    },
    setOrderProductCostV2: (
      state,
      action: PayloadAction<{
        id: PBUUID
        oneTimeCostV2: PBFractionalMoney
        isCostOverridden?: boolean
      }>
    ) => {
      if (!state.products) {
        state.products = []
      }

      const existingProductIndex = state.products?.findIndex(
        (product) =>
          product.id &&
          UUID.fromPB(product.id).eq(UUID.fromPB(action.payload.id))
      )

      if (existingProductIndex >= 0) {
        state.products[existingProductIndex] = {
          ...state.products[existingProductIndex],
          costV2: action.payload.oneTimeCostV2,
          isCostOverridden: action.payload.isCostOverridden ?? false,
        }
        markProductEdited(state, state.products[existingProductIndex])
      }

      state.isEdited = checkOrderIsEdited(state)
    },
    clearOrderProductCostOverride: (state, action: PayloadAction<PBUUID>) => {
      const index = state.products?.findIndex(
        (product) =>
          product.id && UUID.fromPB(product.id).eq(UUID.fromPB(action.payload))
      )
      if (index !== undefined && index >= 0 && state.products) {
        state.products[index] = {
          ...state.products[index],
          costV2: undefined,
          isCostOverridden: false,
        }
        markProductEdited(state, state.products[index])
      }
      state.isEdited = checkOrderIsEdited(state)
    },
    setProductReceivedQuantitiesToZero: (
      state,
      action: PayloadAction<{ orderStatus?: OrderStatus }>
    ) => {
      // Drafts (e.g. receiving a vendor-website shipment) edit and submit
      // quantityOrderedV2, so it must be zeroed too.
      const isDraft = isDraftOrderStatus(action.payload.orderStatus)
      for (const product of state.products ?? []) {
        if (isDraft) {
          product.quantityOrderedV2 = Decimal.zero()
        }
        product.quantityStagedReceived = Decimal.zero()
      }
      recomputeProductsEdited(state)
      state.isEdited = checkOrderIsEdited(state)
    },
    resetSearchProductSelected: (state) => {
      delete state.searchProduct
      state.isEdited = checkOrderIsEdited(state)
    },
    setAddProductQuantity: (state, action: PayloadAction<Decimal>) => {
      state.addProductQuantity = action.payload
      state.isEdited = checkOrderIsEdited(state)
    },
    setIsPreloadingProducts: (state, action: PayloadAction<boolean>) => {
      state.isPreloadingProducts = action.payload
    },
    setDropshipAddress: (state, action: PayloadAction<Address>) => {
      state.dropshipAddress = action.payload
    },
    setDropshipRecipient: (state, action: PayloadAction<string>) => {
      state.dropshipRecipient = action.payload
    },
    setOrderReceiveSettingsOverride: (
      state,
      action: PayloadAction<OrderReceiveSettings>
    ) => {
      state.orderReceiveSettingsOverride = action.payload
    },
    setOrderPromoCode: (state, action: PayloadAction<string | undefined>) => {
      state.promoCode = action.payload
      state.isEdited = checkOrderIsEdited(state)
    },
    setShipmentNotification: (
      state,
      action: PayloadAction<{
        shipmentNotification: ListOrderNotificationsForOrderRes_ShipmentNotification | null
        products: Record<string, ListOrderNotificationsForOrderRes_Product>
        vendor?: Vendor
        orderStatus?: OrderStatus
      }>
    ) => {
      if (!action.payload.shipmentNotification) {
        state.shipmentNotification = undefined
        state.orderReceiveSettingsOverride = undefined
        state.isEdited = checkOrderIsEdited(state)
        return state
      }

      if (!state.products) {
        state.products = []
      }
      const snProducts = mergeShipmentNotificationProducts(
        action.payload.shipmentNotification.shipmentNotification?.products ?? []
      )
      const txnProductsById = action.payload.products
      for (const product of snProducts) {
        if (!product.productId) {
          continue
        }

        const existingProductIndex =
          state.products?.findIndex(
            (p) =>
              p.productId &&
              product.productId &&
              UUID.fromPB(p.productId).eq(UUID.fromPB(product.productId))
          ) ?? -1

        const productCost = product.cost
          ? Decimal.fromPB(product.cost)
          : undefined
        const cost = productCost
          ? FractionalMoney.fromDollars(productCost.toString())
          : undefined

        const txnProduct =
          txnProductsById[UUID.fromPB(product.productId).toString()]
        if (!txnProduct || !txnProduct.product) {
          continue
        }

        if (existingProductIndex >= 0 && state.products) {
          state.products[existingProductIndex] = {
            ...state.products[existingProductIndex],
            quantityOrderedV2:
              action.payload.orderStatus === OrderStatus.ORDERED
                ? state.products[existingProductIndex].quantityOrderedV2
                : Decimal.fromBigSource(product.quantityShipped),
            quantityStagedReceived: Decimal.fromBigSource(
              product.quantityShipped
            ),
            costV2: cost ?? state.products[existingProductIndex].costV2,
            isCostOverridden: !!cost,
          }
        } else {
          state.products = [
            ...(state.products ?? []),
            {
              id: UUID.newRandom(),
              productId: product.productId,
              quantityOrderedV2:
                action.payload.orderStatus === OrderStatus.ORDERED
                  ? Decimal.zero()
                  : Decimal.fromBigSource(product.quantityShipped),
              quantityStagedReceived:
                action.payload.orderStatus === OrderStatus.ORDERED
                  ? Decimal.fromBigSource(product.quantityShipped)
                  : Decimal.zero(),
              position: (state.products?.length ?? 0) + 1,
              costV2: cost,
              notes: "",
              additionalFeesWhenReceived: [],
              saleIdentifier: "",
              saleId: undefined,
              customFields: [],
              isSpecialOrder: false,
              isCostOverridden: !!cost,
              reasoningNotes: "",
            },
          ]
        }

        state = updateOrderFormWithProductData(state, txnProduct)
      }

      // Set quantities to 0 for products not in the shipment notification
      for (let i = 0; i < (state.products?.length ?? 0); i++) {
        const product = state.products?.[i]
        if (!product) {
          continue
        }

        const isInShipment =
          snProducts?.some(
            (p) =>
              p.productId &&
              product.productId &&
              UUID.fromPB(p.productId).eq(UUID.fromPB(product.productId))
          ) ?? false

        if (!isInShipment && state.products) {
          state.products[i] = {
            ...product,
            // quantity ordered should stay the same if the order status is ORDERED
            quantityOrderedV2:
              action.payload.orderStatus === OrderStatus.ORDERED
                ? product.quantityOrderedV2
                : Decimal.zero(),
            quantityStagedReceived: Decimal.zero(),
          }
        }
      }

      if (action.payload?.vendor) {
        const { vendor } = action.payload
        state.vendor = vendor
        state.method = methodForShipmentVendor(
          state.method,
          vendor,
          action.payload.orderStatus
        )
      }

      state.shipmentNotification = action.payload.shipmentNotification
      // Seed the override from the vendor's defaults so the receive bar shows what
      // would happen; edits to it apply to this receive only.
      state.orderReceiveSettingsOverride = OrderReceiveSettings.clone(
        state.vendor?.orderReceiveSettings ?? OrderReceiveSettings.create()
      )
      recomputeProductsEdited(state)
      state.isEdited = checkOrderIsEdited(state)
      return state
    },
    preloadProducts: (
      state,
      action: PayloadAction<{
        products: TransactionProduct[]
      }>
    ) => {
      const { products } = action.payload
      for (const product of products) {
        selectOrderProduct(state, product)
        // Pass quantity directly to avoid intermediate state mutations
        submitOrderProduct(
          state,
          OrderStatus.UNDEFINED,
          product.quantity,
          product.uomId,
          product.uomSnapshot,
          true
        )
      }
      state.isPreloadingProducts = true
    },
    setOrderInvoiceDate: (state, action: PayloadAction<Timestamp>) => {
      state.invoiceDate = action.payload
      state.isEdited = checkOrderIsEdited(state)
    },
    setOrderDueDate: (state, action: PayloadAction<Timestamp>) => {
      state.dueDate = action.payload
      state.isEdited = checkOrderIsEdited(state)
    },
    setOrderInvoiceNumber: (state, action: PayloadAction<string>) => {
      state.invoiceNumber = action.payload
      state.isEdited = checkOrderIsEdited(state)
    },
    setOrderReferenceNumber: (state, action: PayloadAction<string>) => {
      state.referenceNumber = action.payload
      state.isEdited = checkOrderIsEdited(state)
    },
  },
})

export const {
  setOrderInitialState,
  setOrderInternalNotes,
  setOrderNotes,
  setOrderIsEdited,
  setOrderLocation,
  setOrderProducts,
  setAdditionalFeesByProductId,
  setCatalogProductsById,
  setInventoriesByProductId,
  setProductsById,
  setTierPricesByProductId,
  setOrderRequestedDeliveryDate,
  setOrderVendor,
  setOrderMethod,
  resetOrder,
  searchProductSelected,
  searchProductSubmitted,
  removeOrderProduct,
  setOrderProductQuantity,
  setOrderProductUom,
  setOrderProductUomForProductId,
  setProductCostAndPrice,
  setOrderProductNotes,
  setProductReceivedQuantitiesToZero,
  resetSearchProductSelected,
  setAddProductQuantity,
  setOrderProductCostV2,
  clearOrderProductCostOverride,
  setIsPreloadingProducts,
  setDropshipAddress,
  setDropshipRecipient,
  setOrderPromoCode,
  setOrderReceiveSettingsOverride,
  setShipmentNotification,
  preloadProducts,
  setOrderInvoiceDate,
  setOrderDueDate,
  setOrderInvoiceNumber,
  setOrderReferenceNumber,
} = ordersSlice.actions

export const selectOrderInitalState = (state: AppState) =>
  state.orders.initialState
export const selectOrderIsEdited = (state: AppState) => !!state.orders.isEdited

// selectOrderHasUnsavedOrderEdits reports whether the order itself has been
// edited, ignoring staged receive quantities
export const selectOrderHasUnsavedOrderEdits = (state: AppState): boolean => {
  const initial = state.orders.initialState
  if (!initial?.products) {
    return false
  }
  if (state.orders.internalNotes !== initial.internalNotes) {
    return true
  }
  if (state.orders.invoiceNumber !== initial.invoiceNumber) {
    return true
  }
  if (state.orders.referenceNumber !== initial.referenceNumber) {
    return true
  }
  if (!Timestamp.equals(state.orders.invoiceDate, initial.invoiceDate)) {
    return true
  }
  if (!Timestamp.equals(state.orders.dueDate, initial.dueDate)) {
    return true
  }
  const current = state.orders.products ?? []
  if (current.length !== initial.products.length) {
    return true
  }
  return Object.keys(state.orders.dirtyOrderProductIds ?? {}).length > 0
}
export const selectOrderInternalNotes = (state: AppState) =>
  state.orders.internalNotes
export const selectOrderNotes = (state: AppState) => state.orders.orderNotes
export const selectOrderLocation = (state: AppState) => state.orders.location
export const selectOrderProducts = (state: AppState) => state.orders.products
export const selectOrderRequestedDeliveryDate = (state: AppState) =>
  state.orders.requestedDeliveryDate
export const selectOrderVendor = (state: AppState) => state.orders.vendor
export const selectOrderMethod = (state: AppState) => state.orders.method
export const selectOrderAdditionalFeesByProductId = (state: AppState) =>
  state.orders.additionalFeesByProductId
export const selectOrderProductsById = (state: AppState) =>
  state.orders.productsById
export const selectOrderCatalogProductsById = (state: AppState) =>
  state.orders.catalogProductsById
export const selectOrderInventoriesByProductId = (state: AppState) =>
  state.orders.inventoriesByProductId
export const selectOrderIsSubmittable = ({
  orders: { products, vendor, location },
}: AppState) => (products ?? []).length >= 1 && vendor && location
export const selectLastModifiedOrderProduct = (state: AppState) =>
  state.orders.lastModifiedOrderProduct
export const selectSearchProduct = (state: AppState) =>
  state.orders.searchProduct
export const selectAddProductQuantity = (state: AppState) =>
  state.orders.addProductQuantity
export const selectOrderTierPricesById = (state: AppState) =>
  state.orders.tierPricesByProductId
export const selectOrderImageUrlsByProductId = (state: AppState) =>
  state.orders.imageUrlsByProductId
export const selectDropshipAddress = (state: AppState) =>
  state.orders.dropshipAddress
export const selectDropshipRecipient = (state: AppState) =>
  state.orders.dropshipRecipient
export const selectShipmentNotification = (state: AppState) =>
  state.orders.shipmentNotification

export const selectOrderReceiveSettingsOverride = (state: AppState) =>
  state.orders.orderReceiveSettingsOverride
export const selectOrderPromoCode = (state: AppState) => state.orders.promoCode
export const selectOrderInvoiceDate = (state: AppState) =>
  state.orders.invoiceDate
export const selectOrderDueDate = (state: AppState) => state.orders.dueDate
export const selectOrderInvoiceNumber = (state: AppState) =>
  state.orders.invoiceNumber
export const selectOrderReferenceNumber = (state: AppState) =>
  state.orders.referenceNumber

function selectOrderProduct(state: OrderFormState, result: TransactionProduct) {
  if (!result || !result?.product?.id) {
    return
  }
  state.searchProduct = result

  if (result.isScan && result.quantity.gt(Decimal.zero())) {
    state.addProductQuantity = state.searchProduct.quantity
  }

  state.isEdited = checkOrderIsEdited(state)
}

// EDI lines never carry UOM fields (stripped on every upsert; never set by
// the backend), so a UOM-resolved scan of a product already on an EDI order
// would always fail the exact uomId match and append a duplicate line.
// Non-EDI orders keep strict UOM equality — a base-units line there can be a
// deliberate user choice.
function findExistingProductIndex(
  state: OrderFormState,
  payloadId: string,
  submittedUomId?: PBUUID
): number {
  const products = state.products ?? []
  const exactIndex = products.findIndex((orderProduct) => {
    if (!orderProduct.productId) return false
    if (UUID.idString(orderProduct.productId) !== payloadId) return false
    return UUID.eqFromPB(orderProduct.uomId, submittedUomId)
  })
  if (exactIndex >= 0 || !submittedUomId || state.method !== Order_Method.EDI) {
    return exactIndex
  }
  return products.findIndex(
    (orderProduct) =>
      orderProduct.productId &&
      UUID.idString(orderProduct.productId) === payloadId &&
      !orderProduct.uomId
  )
}

function setLastModifiedOrderProduct(
  state: OrderFormState,
  id: PBUUID | undefined,
  suppress: boolean | undefined
) {
  if (suppress || !id) return
  state.lastModifiedOrderProduct = { id }
}

function submitOrderProduct(
  state: OrderFormState,
  status?: OrderStatus,
  quantity?: Decimal,
  submittedUomId?: PBUUID,
  submittedUomSnapshot?: LedgerEntry_UomSnapshot,
  // Bulk preloads merge many lines at once; a scroll/flash there would point
  // at an arbitrary row.
  suppressLastModified?: boolean
) {
  // Use provided quantity or fall back to state.addProductQuantity
  const productQuantity = quantity ?? state.addProductQuantity
  if (!productQuantity || !state.searchProduct?.product?.id) {
    return
  }

  state.products ??= []
  state.productsById ??= {}
  state.catalogProductsById ??= {}
  state.inventoriesByProductId ??= {}
  state.tierPricesByProductId ??= {}

  const payloadId = UUID.fromPB(state.searchProduct.product.id).toString()

  const alwaysAddNew =
    state.searchProduct?.product.isSpecialOrderableV2 ||
    state.searchProduct?.product.shouldNotNest

  const inputQuantityToAdd = productQuantity
  const productForUom: Product | undefined =
    state.productsById?.[payloadId] ?? state.searchProduct.product

  const isSubmittedUomValid =
    !!submittedUomId && isValidUomSnapshot(submittedUomSnapshot)
  const submittedUomIdForSelection = isSubmittedUomValid
    ? submittedUomId
    : undefined
  const submittedUomSnapshotForSelection = isSubmittedUomValid
    ? submittedUomSnapshot
    : undefined

  const existingProductIndex = findExistingProductIndex(
    state,
    payloadId,
    submittedUomIdForSelection
  )
  const shouldUpdateExisting = existingProductIndex >= 0 && !alwaysAddNew
  const existingOrderProduct = shouldUpdateExisting
    ? state.products[existingProductIndex]
    : undefined

  const baseQuantityToAdd = computeBaseQuantityToAdd(
    inputQuantityToAdd,
    alwaysAddNew ? -1 : existingProductIndex,
    state.products,
    productForUom,
    submittedUomIdForSelection,
    submittedUomSnapshotForSelection
  )

  if (shouldUpdateExisting && existingOrderProduct) {
    state.products[existingProductIndex] = applyOrderUomSelection({
      orderProduct: updateOrderProductOnSubmit({
        orderProduct: existingOrderProduct,
        baseQuantityToAdd,
        status,
        nextPosition: state.products.length + 1,
      }),
    })
    setLastModifiedOrderProduct(
      state,
      existingOrderProduct.id,
      suppressLastModified
    )
  } else {
    // EDI saves strip UOM fields; create the line the way it will persist so
    // the next scan of any of this product's barcodes can merge into it.
    const keepUom = state.method !== Order_Method.EDI
    const newOrderProduct: OrderProduct = {
      id: UUID.newRandom(),
      productId: state.searchProduct.product.id,
      quantityOrderedV2:
        status !== OrderStatus.ORDERED ? baseQuantityToAdd : Decimal.zero(),
      quantityReceivedV2: Decimal.zero(),
      quantityStagedReceived:
        status === OrderStatus.ORDERED ? baseQuantityToAdd : Decimal.zero(),
      position: state.products.length + 1,
      notes: state.searchProduct.product.templateOrderProductNotes || "",
      additionalFeesWhenReceived: [],
      saleIdentifier: "", // "stock" products will not be linked to a sale
      saleId: undefined,
      customFields: [],
      isSpecialOrder: state.searchProduct.product.isSpecialOrderableV2,
      isCostOverridden: false,
      reasoningNotes: "",
      uomId: keepUom ? submittedUomIdForSelection : undefined,
      uomSnapshot: keepUom ? submittedUomSnapshotForSelection : undefined,
    }
    state.products = [
      ...state.products,
      applyOrderUomSelection({ orderProduct: newOrderProduct }),
    ]
    setLastModifiedOrderProduct(state, newOrderProduct.id, suppressLastModified)
  }

  state = updateOrderFormWithProductData(state, state.searchProduct)

  delete state.searchProduct
  state.addProductQuantity = Decimal.fromBigSource(1)
  recomputeProductsEdited(state)
  state.isEdited = checkOrderIsEdited(state)
}

function computeBaseQuantityToAdd(
  inputQuantityToAdd: Decimal,
  existingProductIndex: number,
  existingProducts: OrderProduct[],
  productForUom?: Product,
  submittedUomId?: PBUUID,
  submittedUomSnapshot?: LedgerEntry_UomSnapshot
): Decimal {
  const existing =
    existingProductIndex >= 0
      ? existingProducts[existingProductIndex]
      : undefined

  if (existing?.uomId && isValidUomSnapshot(existing.uomSnapshot)) {
    return convertUoMQuantityToQuantity(
      inputQuantityToAdd,
      getOrderUomInfo({ orderProduct: existing }).conversionFactorToBase
    )
  }

  if (submittedUomId && isValidUomSnapshot(submittedUomSnapshot)) {
    return convertUoMQuantityToQuantity(
      inputQuantityToAdd,
      submittedUomSnapshot.conversionFactorToBase &&
        Decimal.fromPB(submittedUomSnapshot.conversionFactorToBase)
    )
  }

  return convertUoMQuantityToQuantity(
    inputQuantityToAdd,
    getConversionFactorForUom(
      productForUom,
      getOrderDefaultUomId(productForUom)
    )
  )
}
